/**
 * How an attendance record should READ, as opposed to what it stores.
 *
 * event_registrations.overall_attendance defaults to 'absent' the moment a
 * member registers, and the per-day rows are equally blank until someone is
 * scanned in. Rendered literally, that tells a member they failed to attend an
 * event that has not happened yet — which is both wrong and alarming.
 *
 * So "absent" is treated as a claim that only becomes true once the event (or
 * the individual day) is behind us. Before that it means "not yet", and while
 * the event is running it means "not checked in yet". Anything actually marked
 * — present, partial — is a fact and passes through untouched.
 */
export type AttendanceTone = "present" | "partial" | "absent" | "pending" | "upcoming";

export type AttendanceDisplay = {
  tone: AttendanceTone;
  label: string;
  /** False while the outcome is still undecided — the caller can stay quiet. */
  isSettled: boolean;
};

/** Today in the viewer's own timezone, as YYYY-MM-DD. */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Event dates arrive as plain YYYY-MM-DD calendar dates. Compared as strings
 * rather than parsed: `new Date("2026-08-20")` is midnight UTC, which in Lagos
 * is still the 20th but in the Americas is the 19th — and an event would flip
 * to "absent" a day early for anyone reading it from the wrong side of the
 * meridian. Lexical order on an ISO date is chronological order.
 */
function dateOnly(value?: string | null): string | null {
  const text = value ? String(value).slice(0, 10) : "";
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

type EventLike = { start_date?: string | null; end_date?: string | null };

/** The last day of the event — its end date, or its start when it runs once. */
function lastDay(event?: EventLike | null): string | null {
  return dateOnly(event?.end_date) ?? dateOnly(event?.start_date);
}

export function eventHasStarted(event?: EventLike | null): boolean {
  const start = dateOnly(event?.start_date);
  return start ? start <= today() : true;
}

export function eventHasEnded(event?: EventLike | null): boolean {
  const end = lastDay(event);
  return end ? end < today() : false;
}

/** The overall attendance badge for a registration. */
export function attendanceDisplay(
  status: string | null | undefined,
  event?: EventLike | null,
): AttendanceDisplay {
  if (status === "present") return { tone: "present", label: "Present", isSettled: true };
  if (status === "partial") return { tone: "partial", label: "Partial", isSettled: true };

  // Nothing marked. What that means depends entirely on where we are relative
  // to the event.
  if (!eventHasStarted(event)) {
    return { tone: "upcoming", label: "Not yet held", isSettled: false };
  }
  if (!eventHasEnded(event)) {
    return { tone: "pending", label: "Not checked in", isSettled: false };
  }

  return { tone: "absent", label: "Absent", isSettled: true };
}

/** The same judgement for one day of a multi-day event. */
export function dayAttendanceDisplay(
  isPresent: boolean,
  dayDate?: string | null,
): AttendanceDisplay {
  if (isPresent) return { tone: "present", label: "Present", isSettled: true };

  const day = dateOnly(dayDate);
  if (!day) return { tone: "absent", label: "Absent", isSettled: true };

  const now = today();
  if (day > now) return { tone: "upcoming", label: "Upcoming", isSettled: false };
  if (day === now) return { tone: "pending", label: "Not checked in", isSettled: false };

  return { tone: "absent", label: "Absent", isSettled: true };
}

/** Badge colours, keyed by tone. Undecided states stay neutral, never red. */
export const ATTENDANCE_TONE_CLASSES: Record<AttendanceTone, string> = {
  present:  "bg-emerald-50 text-emerald-700 border-emerald-100",
  partial:  "bg-amber-50 text-amber-700 border-amber-100",
  absent:   "bg-rose-50 text-rose-700 border-rose-100",
  pending:  "bg-sky-50 text-sky-700 border-sky-100",
  upcoming: "bg-gray-50 text-gray-500 border-gray-100",
};
